import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Descriptions, Modal, Spin, Table, Tag } from 'antd'

import { findChsiReportVerificationDetail } from '../../common/request'
import ResponseCode from '../../common/response-code'
import {
  CHSI_MAPPED_INFO_LABELS,
  getChsiDetailConclusion,
  getChsiReportTypeLabel
} from '../../common/chsiReportVerificationConstants'

const compareColumns = [
  {
    title: '字段',
    dataIndex: 'fieldName',
    key: 'fieldName',
    width: 120
  },
  {
    title: '报名信息',
    dataIndex: 'applyValue',
    key: 'applyValue'
  },
  {
    title: '报告信息',
    dataIndex: 'reportValue',
    key: 'reportValue'
  },
  // {
  //   title: '是否必比',
  //   dataIndex: 'required',
  //   key: 'required',
  //   width: 90,
  //   render: (value) => value ? '是' : '否'
  // },
  {
    title: '是否匹配',
    dataIndex: 'matched',
    key: 'matched',
    width: 90,
    render: (value) => (
      <span className={ value ? 'text-success' : 'text-warning' }>{ value ? '是' : '否' }</span>
    )
  }
]

const ChsiReportVerificationDetailModal = forwardRef((props, ref) => {
  const requestParamsRef = useRef(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  useImperativeHandle(ref, () => ({
    showModal
  }))

  const showModal = (params) => {
    requestParamsRef.current = params
    setModalVisible(true)
    setDetail(null)
    setErrorMessage('')
    fetchDetail(params)
  }

  const hideModal = () => {
    setModalVisible(false)
    setDetail(null)
    setErrorMessage('')
    requestParamsRef.current = null
  }

  const fetchDetail = (params) => {
    if (!params?.applyInfoId || !params?.verificationId) {
      setErrorMessage('缺少核验查询参数')
      return
    }

    setLoading(true)
    setErrorMessage('')

    findChsiReportVerificationDetail(params).then(res => {
      setLoading(false)
      if (res.code === ResponseCode.success) {
        setDetail(res.data || {})
      } else {
        setErrorMessage(res.msg || '查询核验详情失败')
      }
    }).catch(err => {
      setLoading(false)
      const status = err?.response?.status
      if (status === 403) {
        setErrorMessage('无权查看该核验详情')
      } else if (status === 404) {
        setErrorMessage('核验记录不存在或已失效')
      } else {
        setErrorMessage('网络错误，查询核验详情失败')
      }
    })
  }

  const handleRetry = () => {
    if (requestParamsRef.current) {
      fetchDetail(requestParamsRef.current)
    }
  }

  const conclusion = getChsiDetailConclusion(detail)
  const mappedInfoItems = CHSI_MAPPED_INFO_LABELS
    .map(item => ({
      ...item,
      value: detail?.mappedInfo?.[item.key]
    }))
    .filter(item => item.value)

  return (
    <Modal
      title="学信网报告核验详情"
      width={ 860 }
      open={ modalVisible }
      footer={ null }
      onCancel={ hideModal }
      destroyOnClose
    >
      <Spin spinning={ loading }>
        {
          errorMessage
            ? (
              <div className="py-4 text-center">
                <div className="text-danger mb-3">{ errorMessage }</div>
                <a onClick={ handleRetry }>重试</a>
              </div>
            )
            : detail
              ? (
                <div>
                  <div className="mb-3">
                    <Tag color={ conclusion.className.includes('success') ? 'success' : 'warning' }>
                      { conclusion.label }
                    </Tag>
                  </div>

                  <Descriptions
                    title="基本信息"
                    bordered
                    size="small"
                    column={ 1 }
                    className="mb-3"
                  >
                    <Descriptions.Item label="报告类型">
                      { getChsiReportTypeLabel(detail.reportType) }
                    </Descriptions.Item>
                    <Descriptions.Item label="在线验证码">
                      { detail.onlineVerifyCode || '—' }
                    </Descriptions.Item>
                    <Descriptions.Item label="在线核验网址">
                      { detail.manualVerifyUrl || '—' }
                    </Descriptions.Item>
                    <Descriptions.Item label="核验时间">
                      { detail.lastVerifyTime || '—' }
                    </Descriptions.Item>
                    {
                      detail.failReason
                        ? (
                          <Descriptions.Item label="失败原因">
                            { detail.failReason }
                          </Descriptions.Item>
                        )
                        : null
                    }
                  </Descriptions>

                  {
                    mappedInfoItems.length > 0
                      ? (
                        <Descriptions
                          title="报告信息"
                          bordered
                          size="small"
                          column={ 1 }
                          className="mb-3"
                        >
                          {
                            mappedInfoItems.map(item => (
                              <Descriptions.Item label={ item.label } key={ item.key }>
                                { item.value }
                              </Descriptions.Item>
                            ))
                          }
                        </Descriptions>
                      )
                      : null
                  }

                  {
                    detail.fieldCompares?.length > 0
                      ? (
                        <>
                          <div className="mb-2 font-weight-bold">比对结果</div>
                          <Table
                            size="small"
                            bordered
                            rowKey={ (record, index) => `${ record.fieldName }-${ index }` }
                            columns={ compareColumns }
                            dataSource={ detail.fieldCompares }
                            pagination={ false }
                            scroll={{ x: 720 }}
                          />
                        </>
                      )
                      : null
                  }
                </div>
              )
              : (
                !loading && <div className="py-4 text-center text-muted">暂无核验详情</div>
              )
        }
      </Spin>
    </Modal>
  )
})

export default ChsiReportVerificationDetailModal
